/**
 * Die maschinenlesbare Zone von Ausweisen — lesen und prüfen.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM DIESE DATEI OHNE KAMERA UND OHNE ERKENNUNG AUSKOMMT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Sie bekommt fertige Zeilen und gibt Felder zurück. Wer die Zeilen liefert —
 * eine Texterkennung im Browser, ein gekauftes SDK oder ein Mensch, der sie
 * abtippt — ist ihr gleich. Das ist Absicht: die Entscheidung, WOMIT gelesen
 * wird, steht noch aus (siehe `docs/specs/004`), und dieser Teil wird in jedem
 * Fall gebraucht.
 *
 * Er ist damit auch der einzige Teil, der sich vollständig prüfen lässt, ohne
 * einen Reisepass vor eine Kamera zu halten.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  DIE PRÜFZIFFERN SIND DER EIGENTLICHE ZWECK
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Eine Texterkennung verwechselt O und 0, I und 1, S und 5. Ohne Prüfung
 * schreibt so ein Zahlendreher stillschweigend eine falsche Ausweisnummer in
 * eine behördliche Meldung — und auffallen würde das erst, wenn die Gemeinde
 * fragt.
 *
 * Die MRZ trägt für Ausweisnummer, Geburtsdatum und Ablaufdatum je eine
 * Prüfziffer, dazu eine über alles zusammen. Diese Datei wertet sie aus und
 * gibt jedes Feld einzeln als geprüft oder ungeprüft zurück. Der Aufrufer
 * übernimmt nur, was stimmt — lieber ein leeres Feld als ein falsches.
 */

/** Die zwei Bauformen, die im Meldeschein vorkommen. */
export type MrzFormat =
  /** Reisepass: zwei Zeilen zu 44 Zeichen. */
  | 'TD3'
  /** Personalausweis im Scheckkartenformat: drei Zeilen zu 30. */
  | 'TD1'

export interface MrzFeld<T> {
  wert: T
  /** Hat die Prüfziffer gestimmt? Bei Feldern ohne eigene Prüfziffer `null`. */
  geprueft: boolean | null
}

export interface MrzErgebnis {
  format: MrzFormat
  nachname: MrzFeld<string>
  vorname: MrzFeld<string>
  /** `YYYY-MM-DD`, wie überall in diesem Projekt. */
  geburtsdatum: MrzFeld<string>
  staatsangehoerigkeit: MrzFeld<string>
  ausweisart: MrzFeld<string>
  ausweisnummer: MrzFeld<string>
  /** Stimmt die Prüfziffer über alle Felder zusammen? */
  gesamtpruefung: boolean
}

/**
 * Der Wert eines Zeichens in der Prüfziffernrechnung.
 * Ziffern zählen sich selbst, Buchstaben ab 10, das Füllzeichen `<` zählt null.
 */
function wert(z: string): number {
  if (z >= '0' && z <= '9') return z.charCodeAt(0) - 48
  if (z >= 'A' && z <= 'Z') return z.charCodeAt(0) - 55
  if (z === '<') return 0
  // Alles andere kann in einer MRZ nicht vorkommen. Eine Texterkennung liefert
  // es trotzdem gelegentlich — dann soll die Prüfziffer scheitern und nicht
  // eine Ausnahme fliegen.
  return -1
}

/**
 * Die Prüfziffer nach ICAO 9303: Gewichte 7, 3, 1 im Wechsel, Summe modulo 10.
 *
 * Gibt `-1` zurück, wenn ein Zeichen nicht in eine MRZ gehört — dann kann die
 * Ziffer nie stimmen, und genau das ist die gewünschte Auskunft.
 */
export function pruefziffer(s: string): number {
  const gewichte = [7, 3, 1]
  let summe = 0
  for (let i = 0; i < s.length; i++) {
    const v = wert(s[i] ?? '')
    if (v < 0) return -1
    summe += v * (gewichte[i % 3] ?? 1)
  }
  return summe % 10
}

/**
 * Stimmt die mitgelieferte Prüfziffer für diesen Abschnitt?
 *
 * `ziffer` darf fehlen: eine zu kurz erkannte Zeile hat an dieser Stelle nichts,
 * und dann ist „stimmt nicht" die richtige Auskunft — nicht ein Absturz.
 */
function stimmt(abschnitt: string, ziffer: string | undefined): boolean {
  if (ziffer === undefined) return false
  const soll = pruefziffer(abschnitt)
  return soll >= 0 && String(soll) === ziffer
}

/**
 * `YYMMDD` in ein `YYYY-MM-DD`.
 *
 * Das Jahrhundert steht nicht in der MRZ. Für ein GEBURTSDATUM hilft eine
 * Tatsache: es kann nicht in der Zukunft liegen. Ergäbe die Annahme „20xx" ein
 * Datum nach heute, war „19xx" gemeint.
 *
 * NICHT über `new Date(iso)` gerechnet — das liest ein Kalenderdatum als UTC
 * und verschiebt es über Zeitzonen hinweg um einen Tag. Dieselbe Falle, gegen
 * die es in `lib/dates.ts` einen Test gibt.
 */
export function mrzDatum(yymmdd: string, heute = new Date()): string | null {
  if (!/^\d{6}$/.test(yymmdd)) return null
  const jj = Number(yymmdd.slice(0, 2))
  const mm = Number(yymmdd.slice(2, 4))
  const tt = Number(yymmdd.slice(4, 6))
  if (mm < 1 || mm > 12 || tt < 1 || tt > 31) return null

  const jahrhundert = heute.getFullYear() - (heute.getFullYear() % 100)
  let jahr = jahrhundert + jj
  const heuteZahl = heute.getFullYear() * 10000 + (heute.getMonth() + 1) * 100 + heute.getDate()
  if (jahr * 10000 + mm * 100 + tt > heuteZahl) jahr -= 100

  const p = (n: number) => String(n).padStart(2, '0')
  return `${jahr}-${p(mm)}-${p(tt)}`
}

/**
 * Namen aus dem MRZ-Feld: `NACHNAME<<VORNAME<WEITERER<<<<<`.
 *
 * Die Füllzeichen sind keine Leerzeichen, sondern Auffüllung — `<<` trennt
 * Nach- von Vornamen, ein einzelnes `<` trennt Vornamen untereinander.
 */
export function mrzNamen(feld: string): { nachname: string; vorname: string } {
  const [nach = '', vor = ''] = feld.split('<<')
  const saeubern = (s: string) => s.replace(/</g, ' ').replace(/\s+/g, ' ').trim()
  return { nachname: saeubern(nach), vorname: saeubern(vor) }
}

/** Nur A–Z, 0–9 und `<` bleiben — Leerzeichen und Zeilenumbrüche fliegen raus. */
function saeubern(zeile: string): string {
  return zeile.toUpperCase().replace(/[^A-Z0-9<]/g, '')
}

/**
 * Die MRZ lesen.
 *
 * @param zeilen Die erkannten Zeilen, in der Reihenfolge des Ausweises.
 * @returns `null`, wenn es keine erkennbare MRZ ist. Ein Ergebnis mit
 *   `geprueft: false` an einzelnen Feldern heißt: gelesen, aber nicht
 *   verlässlich — der Aufrufer soll es NICHT übernehmen.
 */
export function leseMrz(zeilen: string[], heute = new Date()): MrzErgebnis | null {
  const z = zeilen.map(saeubern).filter((s) => s.length > 0)
  const [a, b, c] = z

  // Die Länge entscheidet über die Bauform: 2×44 ist ein Reisepass, 3×30 ein
  // Ausweis im Scheckkartenformat. Weicht sie ab, war es keine MRZ — dann lieber
  // nichts liefern als etwas raten.
  if (a && b && a.length === 44 && b.length === 44) return td3(a, b, heute)
  if (a && b && c && a.length === 30 && b.length === 30) return td1(a, b, c, heute)
  return null
}

/** Reisepass: zwei Zeilen zu 44. */
function td3(l1: string, l2: string, heute: Date): MrzErgebnis {
  const nummer = l2.slice(0, 9)
  const nummerP = l2[9]
  const nation = l2.slice(10, 13)
  const geb = l2.slice(13, 19)
  const gebP = l2[19]
  const ablauf = l2.slice(21, 27)
  const ablaufP = l2[27]
  const persNr = l2.slice(28, 42)
  const persNrP = l2[42]
  const gesamtP = l2[43]

  const { nachname, vorname } = mrzNamen(l1.slice(5))
  const gebOk = stimmt(geb, gebP)
  const datum = mrzDatum(geb, heute)

  // Die Gesamtprüfziffer läuft über Nummer, Geburtsdatum, Ablauf und
  // Personalnummer samt ihrer Einzelprüfziffern — nicht über die ganze Zeile.
  const gesamt = stimmt(
    l2.slice(0, 10) + l2.slice(13, 20) + l2.slice(21, 43), gesamtP,
  )

  return {
    format: 'TD3',
    // Namen tragen keine eigene Prüfziffer. Sie gehen in die Gesamtprüfung ein
    // — deshalb `null` und nicht `true`: „nicht einzeln prüfbar" ist etwas
    // anderes als „geprüft und in Ordnung".
    nachname: { wert: nachname, geprueft: null },
    vorname: { wert: vorname, geprueft: null },
    geburtsdatum: { wert: datum ?? '', geprueft: gebOk && datum !== null },
    staatsangehoerigkeit: { wert: nation.replace(/</g, ''), geprueft: null },
    ausweisart: { wert: l1.slice(0, 1) === 'P' ? 'passport' : l1.slice(0, 2).replace(/</g, ''), geprueft: null },
    ausweisnummer: { wert: nummer.replace(/</g, ''), geprueft: stimmt(nummer, nummerP) },
    gesamtpruefung: gesamt && stimmt(ablauf, ablaufP) && stimmt(persNr, persNrP),
  }
}

/** Personalausweis im Scheckkartenformat: drei Zeilen zu 30. */
function td1(l1: string, l2: string, l3: string, heute: Date): MrzErgebnis {
  const art = l1.slice(0, 2).replace(/</g, '')
  const nummer = l1.slice(5, 14)
  const nummerP = l1[14]
  const geb = l2.slice(0, 6)
  const gebP = l2[6]
  const ablauf = l2.slice(8, 14)
  const ablaufP = l2[14]
  const nation = l2.slice(15, 18)
  const gesamtP = l2[29]

  const { nachname, vorname } = mrzNamen(l3)
  const gebOk = stimmt(geb, gebP)
  const datum = mrzDatum(geb, heute)

  const gesamt = stimmt(
    l1.slice(5, 30) + l2.slice(0, 7) + l2.slice(8, 15) + l2.slice(18, 29), gesamtP,
  )

  return {
    format: 'TD1',
    nachname: { wert: nachname, geprueft: null },
    vorname: { wert: vorname, geprueft: null },
    geburtsdatum: { wert: datum ?? '', geprueft: gebOk && datum !== null },
    staatsangehoerigkeit: { wert: nation.replace(/</g, ''), geprueft: null },
    ausweisart: { wert: art === 'I' || art === 'ID' ? 'id_card' : art, geprueft: null },
    ausweisnummer: { wert: nummer.replace(/</g, ''), geprueft: stimmt(nummer, nummerP) },
    gesamtpruefung: gesamt && stimmt(ablauf, ablaufP),
  }
}

/**
 * Was davon darf in das Formular übernommen werden?
 *
 * Die Regel ist einfach und bewusst streng: **ein Feld mit gescheiterter
 * Prüfziffer wird nicht übernommen.** Lieber ein leeres Feld, das der Gast
 * ausfüllt, als ein falsches, das er nicht bemerkt.
 *
 * Felder ohne eigene Prüfziffer — Namen, Staatsangehörigkeit — hängen an der
 * Gesamtprüfung. Stimmt die nicht, wurde irgendwo falsch gelesen, und dann ist
 * auch dem Namen nicht zu trauen.
 */
export function uebernehmbar(m: MrzErgebnis): Partial<Record<
  'first_name' | 'last_name' | 'birth_date' | 'nationality' | 'document_type' | 'document_number',
  string
>> {
  const raus: ReturnType<typeof uebernehmbar> = {}
  if (m.ausweisnummer.geprueft && m.ausweisnummer.wert) raus.document_number = m.ausweisnummer.wert
  if (m.geburtsdatum.geprueft && m.geburtsdatum.wert) raus.birth_date = m.geburtsdatum.wert
  if (m.gesamtpruefung) {
    if (m.nachname.wert) raus.last_name = m.nachname.wert
    if (m.vorname.wert) raus.first_name = m.vorname.wert
    if (m.staatsangehoerigkeit.wert) raus.nationality = m.staatsangehoerigkeit.wert
    if (m.ausweisart.wert) raus.document_type = m.ausweisart.wert
  }
  return raus
}
